import { isRetiredImagerySource } from "~/components/RetiredImagery/retired-imagery"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { siteRemoteState } from "~/containers/Proposal/state/site/site-state"
import type { Imagery } from "~/graphql/client"
import { ImagerySource } from "~/graphql/client"

import type { TransactionOperation } from "."
import type { IPlacedImage } from "../../imageElements"
import { mapImageToKey, findImageState } from "../../imageElements"

function byCreatedAtDesc(
  a: { __typename: `PoisonedImagery` } | { createdAt: Date | string },
  b: { __typename: `PoisonedImagery` } | { createdAt: Date | string },
) {
  if (
    (`__typename` in a && a.__typename === `PoisonedImagery`) ||
    (`__typename` in b && b.__typename === `PoisonedImagery`)
  ) {
    return 0
  }
  if (`createdAt` in a && `createdAt` in b) {
    return (
      new Date(b.createdAt ?? ``).getTime() -
      new Date(a.createdAt ?? ``).getTime()
    )
  }

  return 0
}

function isImagery(i: unknown): i is Imagery {
  return (
    i !== undefined &&
    i !== null &&
    typeof i === `object` &&
    `__typename` in i &&
    i.__typename === `Imagery`
  )
}

export const getPreferredImage: TransactionOperation<
  undefined,
  IPlacedImage
> = ({ get }) => {
  const { displayImageryProvider: displaySource, source: designSource } =
    get(designLocalState)

  // find image by selected key first
  const display = get(findImageState(displaySource))
  if (display.filename) {
    return display
  }

  // if old format try mapping key from provider:
  const { imagery } = get(siteRemoteState)
  const [di] = imagery
    .filter((i) => i.__typename === `Imagery` && i.source === displaySource)
    .sort(byCreatedAtDesc)
  if (isImagery(di)) {
    const displayKey = mapImageToKey(di)
    const display = get(findImageState(displayKey))
    if (display.filename) {
      return display
    }
  }

  // A Vision Bing Design without its saved image has no substitute background. An Aurora
  // Design's provider is Aurora metadata, so it keeps the fallback below.
  if (isRetiredImagerySource(displaySource) && designSource !== `aurora`) {
    return get(findImageState(``))
  }

  // if it hasn't been set then go through the
  // list of imagery and find the first one that has a filename
  // in order of nearmap, google. Bing is retired and never picked automatically.

  const [ni] = imagery
    .filter(
      (i) => i.__typename === `Imagery` && i.source === ImagerySource.nearmap,
    )
    .sort(byCreatedAtDesc)
  if (isImagery(ni)) {
    const nearmapKey = mapImageToKey(ni)
    const nearmap = get(findImageState(nearmapKey))

    if (nearmap.filename) {
      return nearmap
    }
  }

  const [gi] = imagery
    .filter(
      (i) => i.__typename === `Imagery` && i.source === ImagerySource.google,
    )
    .sort(byCreatedAtDesc)
  if (isImagery(gi)) {
    const googleKey = mapImageToKey(gi)
    const google = get(findImageState(googleKey))

    return google
  }

  return get(findImageState(``))
}
